import type { StorageAdapter } from './types';

export class BrowserStorageAdapter implements StorageAdapter {
  private readonly storage: Storage | null;

  private readonly memoryFallback = new Map<string, string>();

  constructor(storage: Storage) {
    this.storage = typeof window === 'undefined' ? null : storage;
  }

  get(key: string): string | null {
    if (this.storage) {
      return this.storage.getItem(key);
    }

    return this.memoryFallback.get(key) ?? null;
  }

  set(key: string, value: string): void {
    if (this.storage) {
      this.storage.setItem(key, value);
      return;
    }

    this.memoryFallback.set(key, value);
  }

  remove(key: string): void {
    if (this.storage) {
      this.storage.removeItem(key);
      return;
    }

    this.memoryFallback.delete(key);
  }

  clear(): void {
    // Never clear entire browser storage — namespace removal uses removeKeysMatching.
    if (!this.storage) {
      this.memoryFallback.clear();
    }
  }

  removeKeysMatching(predicate: (key: string) => boolean): void {
    if (this.storage) {
      const keysToRemove: string[] = [];

      for (let index = 0; index < this.storage.length; index++) {
        const key = this.storage.key(index);
        if (key && predicate(key)) {
          keysToRemove.push(key);
        }
      }

      for (const key of keysToRemove) {
        this.storage.removeItem(key);
      }

      return;
    }

    for (const key of this.memoryFallback.keys()) {
      if (predicate(key)) {
        this.memoryFallback.delete(key);
      }
    }
  }
}
